import React, { useState } from 'react';
import { Box, Typography, Button, TextField, Grid, IconButton, Snackbar, Paper, Container, Accordion, AccordionSummary, AccordionDetails } from '@mui/material';
import MailOutlineIcon from '@mui/icons-material/MailOutline';
import PhoneIcon from '@mui/icons-material/Phone';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import TwitterIcon from '@mui/icons-material/Twitter';
import GitHubIcon from '@mui/icons-material/GitHub';
import InstagramIcon from '@mui/icons-material/Instagram';
import FacebookIcon from '@mui/icons-material/Facebook';
import LinkedInIcon from '@mui/icons-material/LinkedIn';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';

import Groups from '@mui/icons-material/Groups';
import AssignmentTurnedInOutlined from '@mui/icons-material/AssignmentTurnedInOutlined';
import PersonAddAlt1 from '@mui/icons-material/PersonAddAlt1';
import { useForm } from 'react-hook-form';
import { motion } from 'framer-motion';

const SectionHeader = ({ tagline, title, subtitle, center = false }) => (
  <Box mb={{ xs: 5, md: 6 }} textAlign={center ? "center" : "left"}>
    {tagline && (
      <Typography variant="overline" sx={{ color: '#1A9E7F', letterSpacing: '0.2em', fontWeight: 700 }}>
        {tagline}
      </Typography>
    )}
    <Typography variant="h3" sx={{ fontWeight: 800, mt: 1, color: '#034522', fontFamily: '"Lato", sans-serif', fontSize: { xs: '2rem', md: '3rem' } }}>
      {title}
    </Typography>
    {subtitle && (
      <Typography variant="h6" sx={{ mt: 2, color: '#333', maxWidth: '700px', mx: center ? 'auto' : 0, lineHeight: 1.6, fontWeight: 400, fontSize: { xs: '1rem', md: '1.25rem' } }}>
        {subtitle}
      </Typography>
    )}
  </Box>
);

function Contact() {
  const { register, handleSubmit, formState: { errors }, reset } = useForm();
  const [openSnackbar, setOpenSnackbar] = useState(false);

  const onSubmit = async (data) => {
    try {
      const response = await fetch("https://formsubmit.co/ajax/growaitech@gmail.com", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Accept": "application/json"
        },
        body: JSON.stringify({
          name: `${data.firstName} ${data.lastName}`,
          email: data.email,
          phone: data.phone,
          message: data.message,
          _subject: `New Inquiry: ${data.firstName} ${data.lastName}`,
          _template: "table",
          _captcha: "false"
        })
      });

      if (response.ok) {
        setOpenSnackbar(true);
        reset();
      } else {
        alert("Something went wrong. Please try again.");
      }
    } catch (error) {
      console.error("Form error:", error);
      alert("Error sending message.");
    }
  };



  return (
    <Box sx={{ overflowX: 'hidden', bgcolor: '#FAFAFA' }}>

      {/* 🔹 HERO SECTION */}
      <Box sx={{ bgcolor: '#002613', color: 'white', pt: { xs: 15, md: 20 }, pb: { xs: 10, md: 20 }, position: 'relative', overflow: 'hidden', textAlign: 'center' }}>
        <Box sx={{ position: 'absolute', top: -100, right: -100, width: { xs: 300, md: 600 }, height: { xs: 300, md: 600 }, background: 'radial-gradient(circle, rgba(26,158,127,0.2) 0%, transparent 70%)', borderRadius: '50%' }} />
        <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1 }}>
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8 }}>
            <Typography variant="overline" sx={{ color: '#1A9E7F', letterSpacing: 3, fontWeight: 700 }}>Global Alliance</Typography>
            <Typography variant="h2" fontWeight={800} sx={{ mt: 2, mb: 3, fontFamily: '"Lato", sans-serif', fontSize: { xs: '2.5rem', md: '3.75rem' } }}>
              Partner With Us
            </Typography>
            <Typography variant="h6" sx={{ opacity: 0.9, maxWidth: '700px', mx: 'auto', lineHeight: 1.6, fontWeight: 300, fontSize: { xs: '1rem', md: '1.25rem' } }}>
              Ready to accelerate your digital transformation? Let's discuss how our engineering expertise can solve your most complex business challenges.
            </Typography>
          </motion.div>
        </Container>
      </Box>

      <Container maxWidth="lg" sx={{ mt: { xs: 0, md: -8 }, position: 'relative', zIndex: 2, mb: 12 }}>
        <Paper
          elevation={20}
          sx={{
            borderRadius: 6,
            overflow: 'hidden',
            bgcolor: '#fff',
            boxShadow: '0 30px 60px rgba(0,0,0,0.1)'
          }}
        >
          <Grid container>
            {/* Contact Info Column */}
            <Grid item xs={12} md={5} sx={{ position: 'relative' }}>
              <Box
                component={motion.div}
                initial={{ opacity: 0, x: -30 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.8 }}
                sx={{
                  bgcolor: '#002613',
                  height: { xs: 'auto', md: '100%' }, // Allow efficient stacking on mobile
                  p: { xs: 3, md: 6 }, // Reduce padding on mobile
                  color: 'white',
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'space-between',
                  background: 'linear-gradient(135deg, #002613 0%, #034522 100%)'
                }}
              >
                <Box>
                  <Typography variant="h5" sx={{ fontWeight: 700, mb: 1 }}>
                    Contact Information
                  </Typography>
                  <Typography variant="body2" sx={{ opacity: 0.7, mb: 6 }}>
                    Fill out the form and our team will get back to you within 24 hours.
                  </Typography>

                  <Box display="flex" alignItems="flex-start" mb={4}>
                    <MailOutlineIcon sx={{ color: '#1A9E7F', mr: 2, mt: 0.5 }} />
                    <Box>
                      <Typography variant="subtitle2" sx={{ opacity: 0.7 }}>Email</Typography>
                      <Typography variant="body1" fontWeight={500} sx={{ wordBreak: 'break-all' }}>growaitech@gmail.com</Typography>
                      <Typography variant="body1" fontWeight={500} sx={{ wordBreak: 'break-all' }}>admin@growaitech.com</Typography>
                    </Box>
                  </Box>

                  <Box display="flex" alignItems="flex-start" mb={4}>
                    <PhoneIcon sx={{ color: '#1A9E7F', mr: 2, mt: 0.5 }} />
                    <Box>
                      <Typography variant="subtitle2" sx={{ opacity: 0.7 }}>Phone</Typography>
                      <Typography variant="body1" fontWeight={500}>+91 63745 49599</Typography>
                      <Typography variant="body1" fontWeight={500}>+91 73392 17119</Typography>
                    </Box>
                  </Box>

                  <Box display="flex" alignItems="flex-start" mb={4}>
                    <LocationOnIcon sx={{ color: '#1A9E7F', mr: 2, mt: 0.5 }} />
                    <Box>
                      <Typography variant="subtitle2" sx={{ opacity: 0.7 }}>Headquarters</Typography>
                      <Typography variant="body2" sx={{ lineHeight: 1.6 }}>
                        Door No. 61-4, S.F. No: 25/12 A 2B,<br />
                        Salem Bangalore Byepass Road,<br />
                        Salem, Tamil Nadu 636005
                      </Typography>
                    </Box>
                  </Box>
                </Box>

                <Box sx={{ mt: 4 }}>
                  <Typography variant="subtitle2" sx={{ opacity: 0.7, mb: 2 }}>Follow Us</Typography>
                  <Box display="flex" gap={1}>
                    {[
                      { Icon: LinkedInIcon, link: "https://www.linkedin.com/company/growaitech" },
                      { Icon: TwitterIcon, link: "https://x.com/GrowAiTech" },
                      { Icon: GitHubIcon, link: "https://github.com/growaitech" },
                      { Icon: InstagramIcon, link: "https://www.instagram.com/growaitech" },
                      { Icon: FacebookIcon, link: "https://www.facebook.com/people/growaitech-company/61568267609033/" }
                    ].map(({ Icon, link }, i) => (
                      <IconButton
                        key={i}
                        component="a"
                        href={link}
                        target="_blank"
                        sx={{ color: 'white', bgcolor: 'rgba(255,255,255,0.1)', '&:hover': { bgcolor: '#1A9E7F' } }}
                      >
                        <Icon fontSize="small" />
                      </IconButton>
                    ))}
                  </Box>
                </Box>
              </Box>
            </Grid>

            {/* Form Column */}
            <Grid item xs={12} md={7}>
              <Box
                sx={{ p: { xs: 3, md: 8 } }}
                component={motion.div}
                initial={{ opacity: 0, x: 30 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.8 }}
              >
                <form onSubmit={handleSubmit(onSubmit)}>
                  <Grid container spacing={4}>
                    <Grid item xs={12} sm={6}>
                      <TextField fullWidth label="First Name" variant="standard"
                        {...register('firstName', { required: 'Required' })}
                        error={!!errors.firstName} helperText={errors.firstName?.message}
                        InputLabelProps={{ shrink: true }}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField fullWidth label="Last Name" variant="standard"
                        {...register('lastName', { required: 'Required' })}
                        error={!!errors.lastName}
                        InputLabelProps={{ shrink: true }}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField fullWidth label="Email Address" variant="standard"
                        {...register('email', { required: 'Required' })}
                        error={!!errors.email}
                        InputLabelProps={{ shrink: true }}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField fullWidth label="Phone Number" variant="standard"
                        {...register('phone', { required: 'Required' })}
                        error={!!errors.phone}
                        InputLabelProps={{ shrink: true }}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <TextField fullWidth label="Message" multiline rows={4} variant="standard"
                        {...register('message', { required: 'Required' })}
                        error={!!errors.message}
                        InputLabelProps={{ shrink: true }}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <Button
                        type="submit"
                        variant="contained"
                        size="large"
                        sx={{
                          bgcolor: '#034522',
                          py: 2, px: 6,
                          borderRadius: 50,
                          fontWeight: 700,
                          fontSize: '1rem',
                          boxShadow: '0 10px 20px rgba(3,69,34,0.3)',
                          '&:hover': { bgcolor: '#1A9E7F', transform: 'translateY(-2px)' }
                        }}
                      >
                        Send Message
                      </Button>
                    </Grid>
                  </Grid>
                </form>
              </Box>
            </Grid>
          </Grid>
        </Paper>
      </Container>


      {/* 🔹 PARTNERSHIP & ENGAGEMENT MODELS - NEW SECTION */}
      <Box sx={{ py: { xs: 8, md: 15 }, bgcolor: '#fff', borderBottom: '1px solid #f0f0f0' }}>
        <Container maxWidth="lg">
          <SectionHeader title="Flexible Engagement Models" subtitle="We offer tailored partnership frameworks designed to align with your business goals, budget, and scalability requirements." center />

          <Grid container spacing={4} mt={4}>
            {[
              {
                title: "Dedicated Development Center",
                subtitle: "For Long-Term Scale",
                desc: "Your own extension team in India. We build, manage, and scale a dedicated engineering unit that adopts your culture and processes.",
                features: ["Full Control over Hiring", "Cost Savings up to 60%", "Aligned Time Zones", "Direct Communication"],
                color: "#2979FF",
                icon: <Groups fontSize="large" />
              },
              {
                title: "Project-Based Delivery",
                subtitle: "For Defined Scope",
                desc: "End-to-end product development with a fixed budget and timeline. Ideal for MVPs, migrations, and specific feature implementations.",
                features: ["Guaranteed Delivery Dates", "Fixed Budget", "End-to-End Management", "Rigorous QA included"],
                color: "#00C853",
                icon: <AssignmentTurnedInOutlined fontSize="large" />
              },
              {
                title: "Staff Augmentation",
                subtitle: "For Immediate Needs",
                desc: "Rapidly scale your existing team with our top-tier developers. Fill skill gaps immediately without the overhead of recruitment.",
                features: ["Onboard in 48 Hours", "Top 1% Talent", "Flexible scaling up/down", "Seamless Integration"],
                color: "#FF9800",
                icon: <PersonAddAlt1 fontSize="large" />
              }
            ].map((model, i) => (
              <Grid item xs={12} md={4} key={i}>
                <Paper elevation={0} sx={{ p: 5, height: '100%', borderRadius: 6, bgcolor: '#FAFAFA', borderTop: `6px solid ${model.color}`, transition: '0.3s', '&:hover': { transform: 'translateY(-10px)', boxShadow: '0 20px 40px rgba(0,0,0,0.1)' } }}>
                  <Box sx={{ color: model.color, mb: 3 }}>{model.icon}</Box>
                  <Typography variant="h5" fontWeight={800} sx={{ color: '#034522', mb: 0.5 }}>{model.title}</Typography>
                  <Typography variant="overline" sx={{ color: model.color, fontWeight: 700, letterSpacing: 1 }}>{model.subtitle}</Typography>
                  <Typography variant="body1" sx={{ color: '#555', lineHeight: 1.8, my: 3 }}>{model.desc}</Typography>
                  <Box>
                    {model.features.map((feat, j) => (
                      <Box key={j} display="flex" alignItems="center" mb={1.5}>
                        <CheckCircleIcon sx={{ color: model.color, fontSize: 18, mr: 1.5 }} />
                        <Typography variant="body2" fontWeight={600} color="#444">{feat}</Typography>
                      </Box>
                    ))}
                  </Box>
                </Paper>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* 🔹 ENGAGEMENT PROCESS - IMPRESSIVE & DETAILED */}
      <Box sx={{ py: { xs: 8, md: 15 }, bgcolor: '#002613', color: 'white', position: 'relative', overflow: 'hidden' }}>
        {/* Animated Background Gradients */}
        <Box sx={{ position: 'absolute', top: '-20%', left: '-10%', width: '600px', height: '600px', background: 'radial-gradient(circle, rgba(29, 233, 182, 0.15) 0%, transparent 60%)', borderRadius: '50%' }} />
        <Box sx={{ position: 'absolute', bottom: '-20%', right: '-10%', width: '500px', height: '500px', background: 'radial-gradient(circle, rgba(255, 154, 21, 0.1) 0%, transparent 60%)', borderRadius: '50%' }} />

        <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1 }}>
          <Box textAlign="center" mb={10}>
            <Typography variant="overline" sx={{ color: '#69F0AE', letterSpacing: 3, fontWeight: 700 }}>Workflow</Typography>
            <Typography variant="h3" fontWeight={800} sx={{ mt: 1, fontFamily: '"Lato", sans-serif' }}>From Concept to Reality</Typography>
            <Typography variant="h6" sx={{ opacity: 0.7, maxWidth: '700px', mx: 'auto', mt: 3, fontWeight: 300 }}>
              We don't just write code; we partner with you to engineer digital success. Here is how our proven engagement model works.
            </Typography>
          </Box>

          {/* Engagement Steps */}
          <Grid container spacing={4}>
            {[
              { step: "01", title: "Discovery", text: "We deep-dive into your business goals, user needs, and market landscape to define a laser-focused strategy.", color: "#00E5FF" },
              { step: "02", title: "Design & Architect", text: "Our architects draft scalable systems while designers craft intuitive UI/UX prototypes for your approval.", color: "#E040FB" },
              { step: "03", title: "Agile Development", text: "We build in 2-week sprints, giving you continuous visibility and the flexibility to pivot as needed.", color: "#2979FF" },
              { step: "04", title: "Launch & Scale", text: "Rigorous QA testing aims for a flawless launch, followed by ongoing support to scale your solution.", color: "#00C853" }
            ].map((s, i) => (
              <Grid item xs={12} md={3} key={i}>
                <Box sx={{
                  p: 4,
                  height: '100%',
                  bgcolor: 'rgba(255,255,255,0.03)',
                  borderRadius: 4,
                  border: '1px solid rgba(255,255,255,0.1)',
                  transition: 'all 0.3s',
                  '&:hover': { bgcolor: 'rgba(255,255,255,0.08)', transform: 'translateY(-10px)', borderColor: s.color }
                }}>
                  <Typography variant="h2" fontWeight={900} sx={{ color: 'rgba(255,255,255,0.1)', mb: 2 }}>{s.step}</Typography>
                  <Typography variant="h5" fontWeight={700} sx={{ color: s.color, mb: 2 }}>{s.title}</Typography>
                  <Typography variant="body2" sx={{ opacity: 0.8, lineHeight: 1.7 }}>{s.text}</Typography>
                </Box>
              </Grid>
            ))}
          </Grid>

          {/* Success Metrics Strip */}
          <Box sx={{ mt: 12, pt: 8, borderTop: '1px solid rgba(255,255,255,0.1)' }}>
            <Grid container spacing={4} justifyContent="center" textAlign="center">
              {[
                { label: "Client Retention Rate", value: "95%" },
                { label: "Projects Delivered", value: "100+" },
                { label: "Average Response Time", value: "< 24h" },
                { label: "Support Coverage", value: "24/7" }
              ].map((stat, i) => (
                <Grid item xs={6} md={3} key={i}>
                  <Typography variant="h3" fontWeight={800} sx={{ color: '#fff', mb: 1 }}>{stat.value}</Typography>
                  <Typography variant="overline" sx={{ color: '#69F0AE', fontWeight: 700, letterSpacing: 1 }}>{stat.label}</Typography>
                </Grid>
              ))}
            </Grid>
          </Box>
        </Container>
      </Box>

      <Snackbar
        open={openSnackbar}
        autoHideDuration={6000}
        onClose={() => setOpenSnackbar(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        message={
          <Box display="flex" alignItems="center" gap={1}>
            <CheckCircleIcon sx={{ color: '#4caf50' }} />
            <Typography>Message sent successfully!</Typography>
          </Box>
        }
      />

      {/* 🔹 GLOBAL OFFICES - DETAILED */}
      <Box sx={{ py: { xs: 8, md: 15 }, bgcolor: '#fff' }}>
        <Container maxWidth="lg">
          <Box textAlign="center" mb={10}>
            <Typography variant="overline" color="primary" fontWeight={700} letterSpacing={2}>Our Locations</Typography>
            <Typography variant="h3" fontWeight={800} sx={{ color: '#034522', mt: 1 }}>Global Hubs</Typography>
          </Box>
          <Grid container spacing={4}>
            {[
              { city: "Salem, India", type: "Global Engineering HQ", address: "Door No. 61-4, S.F. No: 25/12 A 2B, Salem Bangalore Byepass Road, Salem, Tamil Nadu 636005", email: "admin@growaitech.com", phone: "+91 63745 49599" }
            ].map((office, i) => (
              <Grid item xs={12} md={6} key={i}>
                <Paper elevation={0} sx={{ p: 4, height: '100%', border: '1px solid #eee', borderRadius: 4, bgcolor: '#FAFAFA', transition: '0.3s', '&:hover': { borderColor: '#1A9E7F', transform: 'translateY(-5px)' } }}>
                  <Box display="flex" alignItems="center" gap={2} mb={2}>
                    <Box sx={{ p: 1.5, bgcolor: '#E8F5E9', borderRadius: '50%', color: '#1A9E7F' }}>
                      <LocationOnIcon />
                    </Box>
                    <Box>
                      <Typography variant="h5" fontWeight={800} color="#034522">{office.city}</Typography>
                      <Typography variant="caption" fontWeight={700} color="primary" sx={{ textTransform: 'uppercase', letterSpacing: 1 }}>{office.type}</Typography>
                    </Box>
                  </Box>
                  <Typography variant="body1" sx={{ mb: 2, color: '#444', lineHeight: 1.6 }}>{office.address}</Typography>
                  <Box display="flex" flexDirection="column" gap={1}>
                    <Box display="flex" alignItems="center" gap={1.5}>
                      <MailOutlineIcon fontSize="small" sx={{ color: '#1A9E7F' }} />
                      <Typography variant="body2" fontWeight={500}>{office.email}</Typography>
                    </Box>
                    <Box display="flex" alignItems="center" gap={1.5}>
                      <PhoneIcon fontSize="small" sx={{ color: '#1A9E7F' }} />
                      <Typography variant="body2" fontWeight={500}>{office.phone}</Typography>
                    </Box>
                  </Box>
                </Paper>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* 🔹 CLIENT VOICES (New Section) */}
      <Box sx={{ py: { xs: 8, md: 15 }, bgcolor: '#002613', color: 'white' }}>
        <Container maxWidth="lg">
          <Typography variant="h3" fontWeight={800} textAlign="center" mb={8}>Voices of Success</Typography>
          <Grid container spacing={4}>
            {[
              { quote: "GrowAITech transformed our legacy infrastructure into a cloud-native powerhouse. We reduced costs by 40% and improved latency by 200%.", author: "Sarah Jenkins", role: "CTO, FinServe Global" },
              { quote: "Their AI team didn't just build a model; they understood our business. The predictive analytics engine they deployed is now our core competitive advantage.", author: "Rajiv Mehta", role: "VP of Product, HealthPlus" },
              { quote: "We have worked with many vendors, but GAT is a true partner. Their transparency and technical depth are unmatched in the industry.", author: "Elena Rossi", role: "Director of LogicStream" }
            ].map((testimonial, i) => (
              <Grid item xs={12} md={4} key={i}>
                <Box sx={{ p: 4, bgcolor: 'rgba(255,255,255,0.05)', borderRadius: 4, height: '100%', position: 'relative' }}>
                  <Typography variant="h1" sx={{ position: 'absolute', top: -20, left: 20, fontSize: '6rem', color: 'rgba(29, 233, 182, 0.1)', fontFamily: 'serif' }}>“</Typography>
                  <Typography variant="body1" sx={{ lineHeight: 1.8, fontSize: '1.1rem', mb: 3, position: 'relative', zIndex: 1 }}>{testimonial.quote}</Typography>
                  <Box borderTop="1px solid rgba(255,255,255,0.1)" pt={2}>
                    <Typography variant="subtitle1" fontWeight={800} color="#69F0AE">{testimonial.author}</Typography>
                    <Typography variant="caption" sx={{ opacity: 0.7 }}>{testimonial.role}</Typography>
                  </Box>
                </Box>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* 🔹 EXPANDED FAQ */}
      <Box sx={{ py: { xs: 8, md: 15 }, bgcolor: '#FAFAFA' }}>
        <Container maxWidth="lg">
          <Typography variant="h3" fontWeight={800} textAlign="center" mb={2} color="#034522">Everything You Need to Know</Typography>
          <Typography variant="h6" color="text.secondary" textAlign="center" mb={8}>Common questions about our process, pricing, and partnerships.</Typography>

          <Grid container spacing={4}>
            <Grid item xs={12} md={6}>
              <Typography variant="h5" fontWeight={700} mb={3} color="primary">Generel & Process</Typography>
              {[
                { q: "Do you work with startups?", a: "Yes! We love startups. We have special packages designed for MVP development that balance speed and scalability." },
                { q: "What methodology do you use?", a: "We primarily use Agile/Scrum with 2-week sprints. This ensures you get regular updates and can pivot if market needs change." },
                { q: "How do we communicate?", a: "We use Slack/Teams for daily comms and Jira/Linear for task tracking. You will have a dedicated Project Manager." },
                { q: "Do I own the code?", a: "100%. Upon final payment, all intellectual property rights and source code are transferred to you." }
              ].map((faq, i) => (
                <Accordion key={i} elevation={0} sx={{ bgcolor: 'white', mb: 2, borderRadius: 2 }}>
                  <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Typography fontWeight={600}>{faq.q}</Typography>
                  </AccordionSummary>
                  <AccordionDetails><Typography color="text.secondary">{faq.a}</Typography></AccordionDetails>
                </Accordion>
              ))}
            </Grid>
            <Grid item xs={12} md={6}>
              <Typography variant="h5" fontWeight={700} mb={3} color="primary">Technical & Security</Typography>
              {[
                { q: "What tech stack do you use?", a: "We are experts in MERN (MongoDB, Express, React, Node), Python (Django/FastAPI), and Cloud (AWS/Azure/GCP)." },
                { q: "How do you handle security?", a: "We follow OWASP Top 10 guidelines, implement strict RBAC, encrypt data at rest/transit, and conduct regular penetration testing." },
                { q: "Can you take over existing code?", a: "Yes. We start with a comprehensive code audit to identify debt and security risks before beginning refactoring or feature additions." },
                { q: "Do you offer SLA?", a: "Yes, for our enterprise clients, we offer SLAs with guaranteed uptime and critical response times as low as 1 hour." }
              ].map((faq, i) => (
                <Accordion key={i} elevation={0} sx={{ bgcolor: 'white', mb: 2, borderRadius: 2 }}>
                  <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Typography fontWeight={600}>{faq.q}</Typography>
                  </AccordionSummary>
                  <AccordionDetails><Typography color="text.secondary">{faq.a}</Typography></AccordionDetails>
                </Accordion>
              ))}
            </Grid>
          </Grid>
        </Container>
      </Box>



    </Box>
  );
}

export default Contact;
